// FabricationOrderAcceptanceFormReadOnly (#70 C8, S50) — read-only rendering of the saved form of a
// single acceptance version, used inside the history dialog to inspect the values of a past attempt
// (e.g. from attempt 9, look at what attempt 4 recorded). It only presents: every field is disabled
// and there are no start/interrupt/close/save actions. The same section building blocks as the
// editable form are reused (single source of truth), so a past attempt looks identical to a live one.
import { Box, Divider, RadioGroup, FormControlLabel, Radio, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material'
import { sectionSx, ChecklistTable, CommitteeRow } from './fabricationOrderAcceptanceFormSections'
import {
  SECTION_1_FIELDS, REQUIRED_DOCUMENTS, FUNCTIONAL_TESTS, emptyChecklist, normalizeCommittee
} from './fabricationOrderAcceptanceProtocol'

const noop = () => {}

// Reuse the same defensive hydration the editable form applies to a saved draft, so a version saved
// before a section existed (or with fewer rows) still renders without crashing.
const checklistRows = (saved, items) =>
  (Array.isArray(saved) && saved.length ? saved : emptyChecklist(items))

/**
 * Props:
 *   form — the parsed form JSON of the version ({ product, section1, documents, tests, conclusions,
 *          finalDecision, committee }); may be empty ({}) for a version with nothing saved.
 *   t_   — translation function.
 */
export default function FabricationOrderAcceptanceFormReadOnly ({ form, t_ }) {
  const safe = form || {}
  const section1 = safe.section1 || {}
  const documents = checklistRows(safe.documents, REQUIRED_DOCUMENTS)
  const tests = checklistRows(safe.tests, FUNCTIONAL_TESTS)
  const committee = normalizeCommittee(safe.committee)
  const finalDecision = safe.finalDecision || ''

  return (
    <Box data-testid='acceptance-version-form-readonly'>
      {/* Section 1 — general info + product */}
      <Box sx={sectionSx}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>
          {t_('Section 1 · General vehicle information')}
        </Typography>
        <TextField
          label={t_('Product')} size='small' fullWidth disabled sx={{ mb: 1.5 }}
          value={safe.product || ''}
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 1.5 }}>
          {SECTION_1_FIELDS.map((field) => (
            <TextField
              key={field.key}
              label={`${field.code} ${t_(field.label)}`}
              size='small' disabled
              value={section1[field.key] || ''}
            />
          ))}
        </Box>
      </Box>

      {/* Section 2 — required documents */}
      <ChecklistTable
        title={t_('Section 2 · Required documents')} items={REQUIRED_DOCUMENTS}
        rows={documents} onChange={noop} disabled t_={t_}
      />

      {/* Section 3 — functional tests */}
      <ChecklistTable
        title={t_('Section 3 · Basic functional tests')} items={FUNCTIONAL_TESTS}
        rows={tests} onChange={noop} disabled t_={t_}
      />

      {/* Section 4 — conclusions, final decision & acceptance committee */}
      <Box sx={sectionSx}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>
          {t_('Section 4 · Conclusions, final decision & signatures')}
        </Typography>
        <TextField
          label={t_('Conclusions / remarks')} multiline minRows={3} fullWidth size='small' disabled
          sx={{ mb: 1.5 }} value={safe.conclusions || ''}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
          <Typography variant='body2' color='text.secondary' sx={{ textTransform: 'uppercase' }}>
            {t_('Final decision')}
          </Typography>
          <RadioGroup row value={finalDecision}>
            <FormControlLabel value='passed' disabled control={<Radio size='small' />} label={t_('Passed')} />
            <FormControlLabel value='failed' disabled control={<Radio size='small' />} label={t_('Failed')} />
          </RadioGroup>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>
          {t_('Committee')}
        </Typography>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell sx={{ color: 'text.secondary' }}>{t_('Rank')}</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>{t_('Name')}</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>{t_('Structure')}</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>{t_('Date')}</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>{t_('Signature')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <CommitteeRow
              label={t_('Authority Leader')} value={committee.authorityLeader}
              onChange={noop} disabled signatureTestId='committee-signature-authority-leader' t_={t_}
            />
            {committee.members.map((member, index) => (
              <CommitteeRow
                key={index} label={`${t_('Member')} ${index + 1}`} value={member}
                onChange={noop} disabled signatureTestId={`committee-signature-member-${index}`} t_={t_}
              />
            ))}
            <CommitteeRow
              label={t_('Contractor representative')} value={committee.contractorRepresentative}
              onChange={noop} disabled signatureTestId='committee-signature-contractor-representative' t_={t_}
            />
          </TableBody>
        </Table>
      </Box>
    </Box>
  )
}
